import { useState, useMemo, useCallback } from "react";
import { SortState, ColumnDef } from "../types";

export function useTableSort<T>(
  data: T[],
  columns: ColumnDef<T>[],
  defaultSort?: SortState,
  onSortChange?: (sort: SortState) => void,
) {
  const [sort, setSort] = useState<SortState>(defaultSort || null);

  const handleSort = useCallback(
    (key: string) => {
      setSort((prev) => {
        let newSort: SortState;

        if (prev?.key === key) {
          // Toggle direction or clear
          if (prev.direction === "asc") {
            newSort = { key, direction: "desc" };
          } else {
            newSort = null; // Clear sort
          }
        } else {
          // New column, start with ascending
          newSort = { key, direction: "asc" };
        }

        onSortChange?.(newSort);
        return newSort;
      });
    },
    [onSortChange],
  );

  const sortedData = useMemo(() => {
    if (!sort) return data;

    const column = columns.find((c) => c.key === sort.key);
    if (!column) return data;

    return [...data].sort((a, b) => {
      // Get raw values
      const aRaw = column.accessor ? column.accessor(a) : (a as any)[sort.key];
      const bRaw = column.accessor ? column.accessor(b) : (b as any)[sort.key];

      // Apply sortValue transform if provided, otherwise use raw value
      // For arrays without sortValue, default to sorting by length
      const aValue = column.sortValue ? column.sortValue(aRaw, a) : Array.isArray(aRaw) ? aRaw.length : aRaw;
      const bValue = column.sortValue ? column.sortValue(bRaw, b) : Array.isArray(bRaw) ? bRaw.length : bRaw;

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sort.direction === "asc" ? 1 : -1;
      if (bValue == null) return sort.direction === "asc" ? -1 : 1;

      // Compare based on type
      let comparison: number;

      if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [data, sort, columns]);

  return {
    sort,
    setSort,
    handleSort,
    sortedData,
  };
}
